import type {
  BoardEvent,
  BoardRouteConnector,
  BoardRouteEdge,
  RouteConnectorPortName,
} from "../../../../../../../../../_logic/calendar-types"
import {
  ROUTE_LANE_SPACING_PX,
  ROUTE_PORT_SLOT_SPACING_PX,
  ROUTE_VISUAL_STROKE_PX,
  ROUTE_VISUAL_GAP_PX,
} from "../../../_shared/_logic/route-constants"
import {
  routeLayoutFrameForWidth,
  routePortCenterX,
  routePortSetForEvent,
  type RouteLayoutFrame,
  type RoutePort,
} from "./routePorts"

const SAME_VERTICAL_STACK_THRESHOLD_PX = 6

type BuildRouteConnectorsInput = {
  edges: BoardRouteEdge[]
  events: BoardEvent[]
  totalWidthPx: number
  hiddenEventId?: string | null
}

type SelectedPorts = {
  start: RouteConnectorPortName
  end: RouteConnectorPortName
}

type ConnectorPlan = {
  edge: BoardRouteEdge
  previousEvent: BoardEvent
  nextEvent: BoardEvent
  selectedPorts: SelectedPorts
}

type LaneReservation = {
  x: number
  yStart: number
  yEnd: number
}

const isSoft = (item: BoardEvent) => item.event.flex_type === "SOFT"

const isVerticalStack = (previous: BoardEvent, next: BoardEvent, frame: RouteLayoutFrame) =>
  Math.abs(routePortCenterX(previous, frame) - routePortCenterX(next, frame)) <=
  SAME_VERTICAL_STACK_THRESHOLD_PX

const displayedGapPx = (previous: BoardEvent, next: BoardEvent, frame: RouteLayoutFrame) => {
  const previousPorts = routePortSetForEvent(previous, frame)
  const nextPorts = routePortSetForEvent(next, frame)

  return nextPorts.IN_TOP.y - previousPorts.OUT_BOTTOM.y
}

const selectPorts = (
  edge: BoardRouteEdge,
  previous: BoardEvent,
  next: BoardEvent,
  frame: RouteLayoutFrame,
): SelectedPorts => {
  if (isSoft(previous) && !isSoft(next)) return { start: "OUT_RIGHT", end: "IN_LEFT" }

  if (!isSoft(previous) && isSoft(next)) return { start: "OUT_LEFT", end: "IN_RIGHT" }

  if (
    edge.overlapMinutes <= 0 &&
    displayedGapPx(previous, next, frame) >= ROUTE_VISUAL_GAP_PX &&
    isVerticalStack(previous, next, frame)
  ) {
    return { start: "OUT_BOTTOM", end: "IN_TOP" }
  }

  if (isVerticalStack(previous, next, frame)) {
    return { start: "OUT_RIGHT", end: "IN_RIGHT" }
  }

  const nextIsRight = routePortCenterX(next, frame) >= routePortCenterX(previous, frame)

  return nextIsRight
    ? { start: "OUT_RIGHT", end: "IN_LEFT" }
    : { start: "OUT_LEFT", end: "IN_RIGHT" }
}

const formatCoord = (value: number) => `${Number(value.toFixed(2))}`

const formatPath = (points: { x: number; y: number }[]) =>
  points
    .map(
      (point, index) =>
        `${index === 0 ? "M" : "L"} ${formatCoord(point.x)} ${formatCoord(point.y)}`,
    )
    .join(" ")

const rangesOverlap = (first: LaneReservation, second: LaneReservation) =>
  Math.max(first.yStart, second.yStart) <= Math.min(first.yEnd, second.yEnd)

const laneConflicts = (candidate: LaneReservation, reservations: LaneReservation[]) =>
  reservations.some(
    (reservation) =>
      Math.abs(reservation.x - candidate.x) < ROUTE_LANE_SPACING_PX &&
      rangesOverlap(candidate, reservation),
  )

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const uniqueCandidates = (values: number[]) =>
  Array.from(new Set(values.map((value) => Number(value.toFixed(2)))))

const laneCandidates = (desiredX: number, minX: number, maxX: number) => {
  const values = [desiredX]
  for (let step = 1; step <= 8; step += 1) {
    values.push(desiredX + step * ROUTE_LANE_SPACING_PX)
    values.push(desiredX - step * ROUTE_LANE_SPACING_PX)
  }

  return uniqueCandidates(values.map((value) => clamp(value, minX, maxX)))
}

const chooseLaneX = ({
  start,
  end,
  frame,
  reservations,
  preferredLaneX = null,
  allowGutterFallback = true,
}: {
  start: RoutePort
  end: RoutePort
  frame: RouteLayoutFrame
  reservations: LaneReservation[]
  preferredLaneX?: number | null
  allowGutterFallback?: boolean
}) => {
  const desiredX = preferredLaneX ?? (start.x + end.x) / 2
  const corridorMin = Math.min(start.x, end.x) + ROUTE_VISUAL_STROKE_PX
  const corridorMax = Math.max(start.x, end.x) - ROUTE_VISUAL_STROKE_PX
  const hasUsableCorridor = corridorMax >= corridorMin
  const preferredLaneMin = clamp(
    desiredX - ROUTE_LANE_SPACING_PX * 2,
    ROUTE_VISUAL_STROKE_PX,
    frame.totalWidthPx - ROUTE_VISUAL_STROKE_PX,
  )
  const preferredLaneMax = clamp(
    desiredX + ROUTE_LANE_SPACING_PX * 2,
    ROUTE_VISUAL_STROKE_PX,
    frame.totalWidthPx - ROUTE_VISUAL_STROKE_PX,
  )
  const minX =
    preferredLaneX != null
      ? preferredLaneMin
      : hasUsableCorridor
        ? corridorMin
        : frame.gutterCenterX
  const maxX =
    preferredLaneX != null
      ? preferredLaneMax
      : hasUsableCorridor
        ? corridorMax
        : allowGutterFallback
          ? frame.gutterCenterX
          : Math.max(minX, frame.totalWidthPx - ROUTE_LANE_SPACING_PX)
  const yStart = Math.min(start.y, end.y)
  const yEnd = Math.max(start.y, end.y)

  for (const x of laneCandidates(desiredX, minX, maxX)) {
    const candidate = { x, yStart, yEnd }

    if (!laneConflicts(candidate, reservations)) {
      reservations.push(candidate)

      return x
    }
  }

  const fallback =
    preferredLaneX == null && allowGutterFallback
      ? hasUsableCorridor
        ? clamp(desiredX, minX, maxX)
        : frame.gutterCenterX
      : clamp(preferredLaneX ?? desiredX, minX, maxX)
  reservations.push({ x: fallback, yStart, yEnd })

  return fallback
}

const connectorPath = (
  ports: SelectedPorts,
  previousEvent: BoardEvent,
  nextEvent: BoardEvent,
  start: RoutePort,
  end: RoutePort,
  frame: RouteLayoutFrame,
  laneReservations: LaneReservation[],
) => {
  // Short cards must not squeeze an arrowhead into a 1–11 px gap or draw through card content.
  if (ports.start === "OUT_RIGHT" && ports.end === "IN_RIGHT") {
    const laneX = Math.min(
      frame.totalWidthPx - ROUTE_VISUAL_STROKE_PX,
      Math.max(start.x, end.x) + 8,
    )

    return formatPath([start, { x: laneX, y: start.y }, { x: laneX, y: end.y }, end])
  }

  const hasSoftAnchor = isSoft(previousEvent) || isSoft(nextEvent)

  if (hasSoftAnchor) {
    const laneX = chooseLaneX({
      start,
      end,
      frame,
      reservations: laneReservations,
      preferredLaneX: isSoft(previousEvent) === isSoft(nextEvent) ? frame.gutterCenterX : null,
    })

    return formatPath([start, { x: laneX, y: start.y }, { x: laneX, y: end.y }, end])
  }

  if (Math.abs(start.x - end.x) <= SAME_VERTICAL_STACK_THRESHOLD_PX) {
    return formatPath([start, end])
  }

  const laneX = chooseLaneX({ start, end, frame, reservations: laneReservations })

  return formatPath([start, { x: laneX, y: start.y }, { x: laneX, y: end.y }, end])
}

const portOffsetFor = (index: number, count: number) =>
  (index - (count - 1) / 2) * ROUTE_PORT_SLOT_SPACING_PX

const portUsageKey = (eventId: string, port: RouteConnectorPortName) => `${eventId}:${port}`

const planEndpointOffsetKey = (plan: ConnectorPlan, endpoint: "start" | "end") =>
  `${plan.edge.id}:${endpoint}`

const buildPortOffsetMap = (plans: ConnectorPlan[]) => {
  const usage = new Map<string, { plan: ConnectorPlan; endpoint: "start" | "end" }[]>()
  for (const plan of plans) {
    const previousId = String(plan.previousEvent.event.id)
    const nextId = String(plan.nextEvent.event.id)
    const startKey = portUsageKey(previousId, plan.selectedPorts.start)
    const endKey = portUsageKey(nextId, plan.selectedPorts.end)
    usage.set(startKey, [...(usage.get(startKey) ?? []), { plan, endpoint: "start" }])
    usage.set(endKey, [...(usage.get(endKey) ?? []), { plan, endpoint: "end" }])
  }

  const offsets = new Map<string, number>()
  for (const entries of usage.values()) {
    const sorted = [...entries].sort((a, b) => {
      const sequenceDelta = a.plan.edge.sequenceIndex - b.plan.edge.sequenceIndex

      if (sequenceDelta !== 0) return sequenceDelta

      return a.plan.edge.id.localeCompare(b.plan.edge.id)
    })
    sorted.forEach((entry, index) => {
      offsets.set(
        planEndpointOffsetKey(entry.plan, entry.endpoint),
        portOffsetFor(index, sorted.length),
      )
    })
  }

  return offsets
}

const applyPortOffset = (
  port: RoutePort,
  portName: RouteConnectorPortName,
  offsetPx: number,
  frame: RouteLayoutFrame,
): RoutePort => {
  if (!offsetPx) return port

  if (portName === "IN_TOP" || portName === "OUT_BOTTOM") {
    return { ...port, x: clamp(port.x + offsetPx, 0, frame.totalWidthPx) }
  }

  return { ...port, y: Math.max(0, port.y + offsetPx) }
}

const connectorAriaLabel = (edge: BoardRouteEdge) => {
  const previous = edge.previousWorkOrderNumber
    ? `Auftrag ${edge.previousWorkOrderNumber}`
    : edge.previousEventLabel
  const next = edge.nextWorkOrderNumber
    ? `Auftrag ${edge.nextWorkOrderNumber}`
    : edge.nextEventLabel

  return `Route zwischen ${previous ?? "Kalendereintrag"} und ${next ?? "Kalendereintrag"}: ${edge.label}`
}

export const buildRouteConnectors = ({
  edges,
  events,
  totalWidthPx,
  hiddenEventId = null,
}: BuildRouteConnectorsInput): BoardRouteConnector[] => {
  if (totalWidthPx <= 0) return []

  const frame = routeLayoutFrameForWidth(totalWidthPx)
  const eventById = new Map(events.map((item) => [String(item.event.id), item]))
  const plans = edges
    .map((edge) => {
      if (edge.previousEventId === hiddenEventId || edge.nextEventId === hiddenEventId) return null

      const previousEvent = eventById.get(edge.previousEventId)
      const nextEvent = eventById.get(edge.nextEventId)

      if (!previousEvent || !nextEvent) return null

      return {
        edge,
        previousEvent,
        nextEvent,
        selectedPorts: selectPorts(edge, previousEvent, nextEvent, frame),
      }
    })
    .filter(Boolean) as ConnectorPlan[]

  const portOffsets = buildPortOffsetMap(plans)
  const laneReservations: LaneReservation[] = []

  return plans
    .sort((a, b) => {
      const sequenceDelta = a.edge.sequenceIndex - b.edge.sequenceIndex

      if (sequenceDelta !== 0) return sequenceDelta

      return a.edge.id.localeCompare(b.edge.id)
    })
    .map((plan) => {
      const previousPorts = routePortSetForEvent(plan.previousEvent, frame)
      const nextPorts = routePortSetForEvent(plan.nextEvent, frame)
      const startPort = applyPortOffset(
        previousPorts[plan.selectedPorts.start],
        plan.selectedPorts.start,
        portOffsets.get(planEndpointOffsetKey(plan, "start")) ?? 0,
        frame,
      )
      const endPort = applyPortOffset(
        nextPorts[plan.selectedPorts.end],
        plan.selectedPorts.end,
        portOffsets.get(planEndpointOffsetKey(plan, "end")) ?? 0,
        frame,
      )
      const path = connectorPath(
        plan.selectedPorts,
        plan.previousEvent,
        plan.nextEvent,
        startPort,
        endPort,
        frame,
        laneReservations,
      )

      return {
        id: plan.edge.id,
        path,
        hitPath: path,
        startPort: plan.selectedPorts.start,
        endPort: plan.selectedPorts.end,
        routeGrade: plan.edge.routeGrade,
        relationKind: plan.edge.relationKind,
        tooltip: plan.edge.tooltip,
        ariaLabel: connectorAriaLabel(plan.edge),
        compact: plan.edge.gapMinutes <= 0 || plan.edge.overlapMinutes > 0,
        dashed:
          plan.edge.previousAnchorKind === "FLEX_WINDOW" ||
          plan.edge.nextAnchorKind === "FLEX_WINDOW" ||
          plan.edge.relationKind.includes("FLEX_WINDOW"),
        missingCoordinates: plan.edge.missingCoordinates,
      }
    })
}
